import Emitter from 'tiny-emitter';

// Defaults options
const defaults = {
  root: '/',
  cacheMaxAge: (60 * 60) // 1 hour
};

// Events name declaration
const NAVIGATE_START = 'navigatestart';
const NAVIGATE_END = 'navigateend';
const NAVIGATE_ERROR = 'navigateerror';
const NAVIGATE_SAME = 'navigatesame';

// Setting up block level variable to store class state , set's to null by default.
let instance = null;

// Cached regex for stripping leading hashes/slashes, trailing spaces and leading './'.
const routeStripper = /^\/*#?\/*(\.\/|)\/*|\s+$/g;

// Cached regex for stripping leading and trailing slashes.
const rootStripper = /^\/+|\/+$/g;

// Cached regex for stripping urls of hash.
const pathStripper = /#.*$/;

// Cached regex for stripping base url
const baseUrlStripper = /^https?:\/\/[a-z\:0-9.\-]+/;

export default class Router extends Emitter {

  /**
   * Class constructor
   * @param  {Hash} options
   *   defaults:
   *   {
   *     root: '/',
   *     cacheMaxAge: (60 * 60)
   *   }
   */
  constructor(options) {
    super();

    if (instance) {
      return instance;
    }

    instance = this;

    // Extend defaults options
    this.options = Object.assign({}, defaults, options);

    // Figure out the initial configuration.
    this.root = this.options.root;
    this._cache = {};
    this._cacheMaxAge = this.options.cacheMaxAge;
    this.fragment = this.getFragment();
    this.popstate = false;
    this.replace = false;

    // Normalize root to always include a leading and trailing slash.
    this.root = (`/${this.root}/`).replace(rootStripper, '/');

    // Listen to the `popstate`event to check the URL state
    window.addEventListener('popstate', this.checkUrl.bind(this), false);

    return instance;
  }

  /**
   * Class public static variables
   */
  static get NAVIGATE_START() {
    return NAVIGATE_START;
  }

  static get NAVIGATE_END() {
    return NAVIGATE_END;
  }

  static get NAVIGATE_ERROR() {
    return NAVIGATE_ERROR;
  }

  static get NAVIGATE_SAME() {
    return NAVIGATE_SAME;
  }

  /**
   * Are we at the app root?
   */
  atRoot() {
    const path = window.location.pathname.replace(/[^\/]$/, '$&/');
    return path === this.root && !this.getSearch();
  }

  /**
   * Replace base URL in given string, if it exists, and return the result.
   *
   * e.g. "http://localhost:8000/api/v1/blah/" becomes "/api/v1/blah/"
   *      "/api/v1/blah/" stays "/api/v1/blah/"
   */
  removeBaseUrl(url) {
    let result = '';

    const match = baseUrlStripper.exec(url);
    if (match) {
      result = match[0];
    }

    if (result.length) {
      url = url.replace(result, '');
    }

    return url;
  }

  /**
   * Unicode characters in `location.pathname` are percent encoded so they're
   * decoded for comparison. `%25` should not be decoded since it may be part
   * of an encoded parameter.
   * @param  {String} fragment
   * @return {String}
   */
  decodeFragment(fragment) {
    return decodeURI(fragment.replace(/%25/g, '%2525'));
  }

  /**
   * Get the pathname and search params, without the root.
   */
  getPath() {
    const path = this.decodeFragment(
      window.location.pathname + window.location.search
    ).slice(this.root.length - 1);
    return path.charAt(0) === '/' ? path.slice(1) : path;
  }

  /**
   * Get the cross-browser normalized URL fragment from the path
   * @param  {String} fragment
   * @return {String}
   */
  getFragment(fragment = null) {
    if (fragment === null) {
      fragment = this.getPath();
    }

    return fragment.replace(routeStripper, '');
  }

  /**
   * Decode htmlentities
   * @param  {String} html
   * @return {String}
   */
  decodeHTML(html) {
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  }

  /**
   * Checks the current URL to see if it has changed, and if it has,
   * calls `navigate`
   * @param  {Event} event [onpopstate event]
   */
  checkUrl() {
    const current = this.getFragment();

    if (current === this.fragment) {
      return false;
    }

    this.navigate(current, {popstate: true});
    return true;
  }

  /**
   * Check the fetch response status
   * @param  {Object} response
   * @return {String} response.text()
   */
  checkStatus(response) {
    if (response.status >= 200 && response.status < 300) {
      return response.text();
    } else {
      const error = new Error(response.statusText);
      error.response = response;
      throw error;
    }
  }

  /**
   * Cache the fetch response with a current timestamp in milliseconds
   * @param  {String} body
   * @return {String} body
   */
  cache(body) {
    const now = Date.now();
    this._cache[this.fragment] = {
      createdAt: now,
      content: body,
    };
    return body;
  }

  /**
   * Retrieve the content from a given fragment.
   * Check if the cache exists and if it has expired or not.
   * @param  {String} fragment
   * @return {Boolean}
   */
  fromCache(fragment) {
    // Check if the fragment exists in the cache store
    if (!(fragment in this._cache)) return false;

    // Retrieve the cache age in seconds
    const now = Date.now();
    const createdAt = this._cache[fragment].createdAt;
    const cacheAge = (now - createdAt) / 1000;

    // If has expires, remove from cache store
    if (cacheAge > this._cacheMaxAge) {
      delete this._cache[fragment];
      return false;
    }

    return true;
  }

  /**
   * Navigate to a given url.
   * Try to retrieve the content from cache otherwise fetch the url.
   * @param  {String} fragment [url to navigate to]
   * @param  {Hash} options  [description]
   */
  navigate(fragment, options = {popstate: false}) {
    // Came from popstate or not
    this.popstate = options.popstate;
    this.replace = options.replace;

    // Remove base url
    fragment = this.removeBaseUrl(fragment || '');

    // Normalize the fragment.
    fragment = this.getFragment(fragment || '');

    // Don't include a trailing slash on the root.
    let root = this.root;
    if (fragment === '' || fragment.charAt(0) === '?') {
      root = root.slice(0, -1) || '/';
    }
    const url = root + fragment;

    // Strip the hash and decode for matching.
    fragment = this.decodeFragment(fragment.replace(pathStripper, ''));

    if (this.fragment === fragment) {
      this.emit(Router.NAVIGATE_SAME);
      return;
    }

    this.fragment = fragment;

    this.emit(Router.NAVIGATE_START);

    if (this.fromCache(this.fragment)) {
      // "defer" cached response long enough for current
      // execution thread to complete
      setTimeout(() => {
        this.fetched(url);
      }, 0);
    } else {
      this.fetch(url);
    }
  }

  /**
   * Fetch the content by making an ajax request on the given url.
   * @param  {String} url [url to fetch]
   */
  fetch(url) {
    fetch(url)
      .then(this.checkStatus.bind(this))
      .then(this.cache.bind(this))
      .then(this.fetched.bind(this, url))
      .catch((error) => {
        this.emit(Router.NAVIGATE_ERROR, error, error.response.status, error.response.statusText);
      });
  }

  /**
   * [fetched description]
   * @param  {String} url
   */
  fetched(url) {
    const html = this._cache[this.fragment].content;

    // Set the fragment as a real URL if not from a popstate event
    if (this.popstate === false) {
      window.history[this.replace ? 'replaceState' : 'pushState']({}, null, url);
    }

    // Change document title
    const match = html.match(/<title>(.*?)<\/title>/);
    const title = this.decodeHTML(match[1].trim());
    document.title = title;

    // Google Analytics track pageview
    if (window.ga && ga.create) {
      ga('set', {page: url, title: title});
      ga('send', 'pageview');
    }

    this.emit(Router.NAVIGATE_END, html, title);
  }
}
